import { ReactElement, memo, useMemo } from 'react';
import { Popup, PopupEvent } from 'react-map-gl';
import { Typography } from '@mui/material';

import { StopsPopupMap, LoadingFallback } from '~components/index';
import { DialogDetailsOrderActiveTab } from '~components/DialogDetailsOrder/types';
import { PopupRequestReturn } from '~hooks/usePopupRequest';
import { ItemInfo } from '~services/item/types';
import { WarehouseInfo } from '~services/warehouse/types';
import { Nullable } from '~globals/types';

import { DashboardVariantV2CarrierContentMapPopupContent } from '../../../../styles';

type DashboardVariantV2CarrierContentInWarehouseTabMapPopupProps = Omit<
  PopupRequestReturn<ItemInfo | WarehouseInfo>,
  'handleOpenPopup'
> & {
  isItem?: boolean;
  onShowDetailOrder?: (
    newData: Nullable<{
      orderId: string;
      currentTab: Nullable<DialogDetailsOrderActiveTab>;
    }>,
  ) => void;
  onOpen?: (event: PopupEvent) => void;
};

const DashboardVariantV2CarrierContentInWarehouseTabMapPopup = ({
  open,
  data,
  coordinates,
  loading,
  error,
  handleClosePopup,
  isItem,
  onShowDetailOrder,
  onOpen,
}: DashboardVariantV2CarrierContentInWarehouseTabMapPopupProps): Nullable<ReactElement> => {
  const itemId = useMemo(
    () => (isItem ? (data as ItemInfo)?.itemId : undefined),
    [data, isItem],
  );

  if (!coordinates || !open) {
    return null;
  }

  return (
    <Popup
      longitude={coordinates[0]}
      latitude={coordinates[1]}
      onOpen={onOpen}
      onClose={handleClosePopup}
      offset={{
        top: [0, 0],
        bottom: [0, -30],
        left: [15, -18],
        right: [-15, -18],
      }}
    >
      <DashboardVariantV2CarrierContentMapPopupContent>
        {loading && <LoadingFallback size={40} />}

        {!loading && data && !error && (
          <StopsPopupMap
            title={data.title}
            address={data.address}
            timeWindow={{
              allDay: data.timeWindowAllDay,
              windowFrom1: data.timeWindowFrom1,
              windowFrom2: data.timeWindowFrom2,
              windowTo1: data.timeWindowTo1,
              windowTo2: data.timeWindowTo2,
            }}
            trackingCode={data.trackingCode}
            reference={data.referenceId}
            itemId={itemId}
            isItem={isItem}
            onShowDetailOrder={onShowDetailOrder}
          />
        )}

        {!loading && !data && error && (
          <Typography variant="body2" textAlign="center">
            No se pudo cargar los datos, intente nuevamente
          </Typography>
        )}
      </DashboardVariantV2CarrierContentMapPopupContent>
    </Popup>
  );
};

export default memo(DashboardVariantV2CarrierContentInWarehouseTabMapPopup);
